import { ApolloError } from '@apollo/client';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLatest } from 'react-use';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  getGraphQLErrorsByStatusCode,
  GraphQLStatusCodes,
  isReconnectSubscriptionError,
} from '@/graphql/errors';
import { useGetState } from '@/hooks/useGetState';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import {
  findPendingInteractiveMessage,
  isPendingInteractiveMessage,
} from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import {
  ChatMode,
  ChatPersona,
  ConversationKind,
  HouseholdRespondToChatMessageInput,
  MessageRole,
  MessageStatus,
  MessageType,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  useHouseholdChatConversationLazyQuery,
  useRespondToHouseholdChatMessageMutation,
  useSendHouseholdChatMessageMutation,
  useStartHouseholdChatStreamMutation,
  useSubscribeHouseholdChatSubscription,
} from '../graphql/HouseholdChat.generated';
import { useHouseholdChatViewedContext } from '../hooks/useHouseholdChatViewedContext';
import { useOpenChatConversationFromDeepLink } from '../hooks/useOpenChatConversationFromDeepLink';
import { useSendInitialHouseholdChatMessage } from '../hooks/useSendInitialHouseholdChatMessage';
import {
  HouseholdChatContext,
  HouseholdChatContextValue,
  HouseholdChatMessage,
  HouseholdChatPendingResponse,
  HouseholdChatSendOptions,
  HouseholdChatTranscriptOptions,
  HouseholdChatView,
} from './householdChat.context';
import {
  appendStreamActivity,
  appendStreamText,
  createOptimisticTurn,
  createStreamingAssistantMessage,
  findStreamingMessage,
  removeOptimisticTurn,
  TEMPLATE_ONLY_MESSAGE_CONTENT,
  toConversationMessages,
  toResultMessages,
  upsertStreamMessage,
} from './householdChatTranscript.utils';

interface HouseholdChatProviderProps {
  children: ReactNode;
  householdId: string;
  /**
   * Limits the chat to one conversation. Use it on surfaces that own one
   * conversation, such as the diagram editor. The chat opens `conversationID`.
   * When `conversationID` is null, the chat starts empty and the first send
   * creates the conversation. The conversation list and New chat are not
   * available.
   */
  singleConversation?: { conversationID: string | null };
  /**
   * Returns input to add to every send, such as the diagram that the user
   * edits. Values in the send options take priority.
   */
  getAdditionalSendInput?: () => HouseholdChatSendOptions['additionalInput'];
  transcriptOptions?: HouseholdChatTranscriptOptions;
}

interface ReconcileAfterReconnectOptions {
  preserveError?: boolean;
}

const PRESENTATION_EDIT_TOOL_NAME = 'edit_presentation';

/**
 * Resolves the answer-depth mode a turn is sent (and tracked) with.
 */
function getSendMode({
  chatMode,
  chatModesEnabled,
  sendPersona,
}: {
  chatMode: ChatMode;
  chatModesEnabled: boolean;
  sendPersona: ChatPersona | null;
}): ChatMode | null {
  // A persona turn ignores mode server-side; don't persist a misleading one.
  // The mode selector stays visible and usable, so the user's answer-depth
  // choice picks up again on the next no-persona turn.
  if (sendPersona) {
    return null;
  }
  // Modes are retired by the no-modes flag; the backend ignores any supplied
  // mode, so sending one would only persist a misleading mode on the message.
  if (!chatModesEnabled) {
    return null;
  }
  return chatMode;
}

export function HouseholdChatProvider({
  children,
  householdId,
  singleConversation,
  getAdditionalSendInput,
  transcriptOptions,
}: HouseholdChatProviderProps) {
  const isTranscriptMessageHidden = transcriptOptions?.isMessageHidden ?? null;
  const isSingleConversation = singleConversation !== undefined;
  const singleConversationID = singleConversation?.conversationID ?? null;
  const latestGetAdditionalSendInput = useLatest(getAdditionalSendInput);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Kind of the active conversation; null until it is known (a brand-new chat,
  // or a selected conversation whose kind the caller didn't supply and whose
  // history hasn't loaded yet). A conversation's kind never changes, so this is
  // only re-synced when the active conversation changes.
  const [conversationKind, setConversationKind] =
    useState<ConversationKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [fullScreenModalHostCount, setFullScreenModalHostCount] = useState(0);
  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null);
  const [messages, setMessages] = useState<HouseholdChatMessage[]>([]);
  const [pendingScrollToMessageId, setPendingScrollToMessageId] = useState<
    string | null
  >(null);
  const [respondingToMessage, setRespondingToMessage] =
    useState<HouseholdChatPendingResponse | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isReconcilingSubscription, setIsReconcilingSubscription] =
    useState(false);
  const [view, setView] = useState<HouseholdChatView>('conversation');
  // Answer-depth mode applied to the next message. Sourced from the active
  // conversation's server-side `mode` (the most recent message that carried
  // one), defaulting to Quick when it has none yet; selecting or starting a
  // conversation re-syncs it, and the mode selector can override it for the
  // next turn. Not persisted across conversations or sessions.
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.Quick);
  // With the no-modes flag on, the answer-depth distinction is retired: the
  // selector is hidden and messages carry no mode (the backend ignores any
  // supplied mode and runs every turn the same way, so sending the local
  // Quick default would only persist a misleading mode on the message).
  const chatModesEnabled = !useFeatureFlag('household_chat_no_modes');
  // Specialized persona applied to the next message; null runs the default
  // chat behavior. Not persisted server-side: selecting or starting another
  // conversation resets it to the default.
  const [chatPersona, setChatPersona] = useState<ChatPersona | null>(null);
  // With the persona POC flag on, the composer shows the "Workflow" persona
  // controls alongside the answer-depth mode selector; persona turns ignore
  // mode, and no-persona turns keep the normal mode behavior.
  const personaSelectorEnabled = useFeatureFlag(
    'household_chat_specialized_persona_poc'
  );

  // The object the user is viewing (from the modal or route), attached to the next
  // message as context. Dismissal is scoped to the viewed object's key:
  // navigating to a different object re-shows the chip (never silently
  // stale), and returning to a dismissed object shows it again too.
  const routeContext = useHouseholdChatViewedContext();
  const [modalViewedContext, setModalViewedContext] = useState<
    ReturnType<typeof useHouseholdChatViewedContext> | undefined
  >(undefined);
  // Undefined restores route context; null explicitly supplies no context.
  const effectiveViewedContext =
    modalViewedContext === undefined ? routeContext : modalViewedContext;
  const [dismissedViewedContextKey, setDismissedViewedContextKey] = useState<
    string | null
  >(null);
  const viewedContextKey = effectiveViewedContext?.key ?? null;
  useEffect(() => {
    // Any change of the viewed object clears the dismissal (including
    // leaving and coming back to the dismissed object).
    setDismissedViewedContextKey((previousKey) =>
      previousKey === viewedContextKey ? previousKey : null
    );
  }, [viewedContextKey]);
  const viewedContext =
    effectiveViewedContext &&
    effectiveViewedContext.key !== dismissedViewedContextKey
      ? effectiveViewedContext
      : null;
  const dismissViewedContext = useCallback(() => {
    setDismissedViewedContextKey(viewedContextKey);
  }, [viewedContextKey]);
  const restoreViewedContext = useCallback(() => {
    setDismissedViewedContextKey(null);
  }, []);
  const hasDismissedViewedContext =
    !!effectiveViewedContext &&
    effectiveViewedContext.key === dismissedViewedContextKey;

  // Bumped whenever the user navigates to a different conversation (select or
  // new); lets async work started before the navigation detect it and abort.
  const navigationVersionRef = useRef(0);
  const subscriptionRestartTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  useEffect(() => {
    return () => clearTimeout(subscriptionRestartTimeoutRef.current);
  }, [conversationId, householdId]);

  // Prevents duplicate submissions before React has rendered the response
  // state that disables the card buttons.
  const respondInFlightRef = useRef(false);
  // True from send start until the send mutation settles. While set, the
  // user's turn may not be persisted yet, so reconnect reconciliation must
  // not infer turn completion from the persisted transcript.
  const sendInFlightRef = useRef(false);
  // Subscription events are not replayed. This ref blocks sends immediately
  // when a disconnect is observed, before React renders the recovery state.
  const subscriptionReconciliationRequiredRef = useRef(false);
  // A send may still be committing when the socket closes. Reconcile after
  // that mutation settles so its persisted turn is included in the snapshot.
  const reconcileAfterSendRef = useRef(false);
  // Only the newest recovery request may apply a fetched snapshot.
  const reconciliationVersionRef = useRef(0);
  // Monotonic count of applied terminal results. A history fetch or response
  // mutation captures this version so a newer authoritative result wins.
  const terminalResultVersionRef = useRef(0);
  // The activity event arrives immediately before tool execution. Keep it
  // until the turn completes so presentation consumers refresh only after the
  // server write has finished.
  const presentationEditInCurrentTurnRef = useRef(false);
  const [
    presentationEditCompletedVersion,
    setPresentationEditCompletedVersion,
  ] = useState(0);

  const completeCurrentTurn = useCallback(() => {
    const presentationWasEdited = presentationEditInCurrentTurnRef.current;
    presentationEditInCurrentTurnRef.current = false;
    if (presentationWasEdited) {
      setPresentationEditCompletedVersion((version) => version + 1);
    }
  }, []);

  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();

  const [startConversation, { loading: isStartingConversation }] =
    useStartHouseholdChatStreamMutation();

  const [loadConversation] = useHouseholdChatConversationLazyQuery({
    // Always refresh to the latest on select; a warm cache lets Apollo
    // dedupe the network round-trip.
    fetchPolicy: 'cache-and-network',
  });

  const [sendChatMessage, { loading: isSendingMessage }] =
    useSendHouseholdChatMessageMutation();

  const [respondToChatMessage] = useRespondToHouseholdChatMessageMutation();

  const pendingInteractiveMessage = useMemo(
    () => findPendingInteractiveMessage(messages),
    [messages]
  );
  const streamingMessage = useMemo(
    () => findStreamingMessage(messages),
    [messages]
  );
  const streamingMessageId = streamingMessage?.id ?? null;

  // The household's shared validation-questions conversations are driven by the
  // agent's question cards: the user answers each one in its card, so there is
  // no free-form turn to take.
  const isValidationQuestionsConversation =
    conversationKind === ConversationKind.ValidationQuestions;

  // Async callbacks need the latest rendered conversation state without
  // manually mirroring each value into its own ref.
  const getConversationState = useGetState({
    conversationId,
    isValidationQuestionsConversation,
    messages,
    pendingInteractiveMessage,
    respondingToMessage,
  });

  const refreshConversation = useCallback(
    async (targetConversationId: string) => {
      const navigationVersion = navigationVersionRef.current;
      const result = await loadConversation({
        variables: { conversationId: targetConversationId },
        fetchPolicy: 'network-only',
      });
      if (result.error) throw result.error;
      if (result.data?.node?.__typename !== 'Conversation') {
        throw new Error('Conversation unavailable');
      }
      const refreshed = toConversationMessages(result.data.node);
      if (
        navigationVersionRef.current === navigationVersion &&
        getConversationState().conversationId === targetConversationId
      ) {
        setMessages(refreshed);
      }
      return refreshed;
    },
    [getConversationState, loadConversation]
  );

  // Re-sync from the database after every resubscription. The WebSocket uses
  // transient pub/sub events, so local state cannot tell whether a card or a
  // terminal result was published while the subscription was disconnected.
  const reconcileAfterReconnect = useCallback(
    async ({ preserveError = false }: ReconcileAfterReconnectOptions = {}) => {
      const currentState = getConversationState();
      const reconnectConversationId = currentState.conversationId;
      const navigationVersion = navigationVersionRef.current;
      if (!reconnectConversationId) {
        return;
      }

      // Do not read before the send transaction commits. Unlike the old recovery
      // path, remember the work instead of relying only on the restarted stream:
      // an event can be published in the resubscription gap too.
      if (sendInFlightRef.current) {
        reconcileAfterSendRef.current = true;
        return;
      }

      reconcileAfterSendRef.current = false;
      const reconciliationVersion = ++reconciliationVersionRef.current;

      try {
        const queryResult = await loadConversation({
          variables: { conversationId: reconnectConversationId },
          fetchPolicy: 'network-only',
        });

        // Ignore snapshots for an old conversation or an older recovery attempt.
        const latestState = getConversationState();
        if (
          navigationVersionRef.current !== navigationVersion ||
          latestState.conversationId !== reconnectConversationId ||
          reconciliationVersionRef.current !== reconciliationVersion
        ) {
          return;
        }

        if (queryResult.error) {
          reportError(
            'Failed to reconcile household chat after reconnect',
            queryResult.error
          );
          setError('Chat stream disconnected.');
          return;
        }

        const node = queryResult.data?.node;
        const conversation = node?.__typename === 'Conversation' ? node : null;
        if (!conversation) {
          reportError(
            'Failed to reconcile household chat after reconnect',
            new Error('Conversation query returned no conversation')
          );
          setError('Chat stream disconnected.');
          return;
        }
        const conversationMessages = toConversationMessages(conversation);
        const lastMessage =
          conversationMessages[conversationMessages.length - 1];
        const completeReconciliation = () => {
          subscriptionReconciliationRequiredRef.current = false;
          setIsReconcilingSubscription(false);
          if (!preserveError) {
            setError(null);
          }
        };

        // A diagram card precedes the final answer; it must not unlock editing.
        if (lastMessage?.type === MessageType.DiagramMessage) {
          setMessages([
            ...conversationMessages,
            findStreamingMessage(latestState.messages) ??
              createStreamingAssistantMessage(),
          ]);
          completeReconciliation();
          return;
        }

        // A trailing user message means the turn is still running. Preserve its
        // local placeholder because the restarted subscription will stream into
        // it. An idle transcript has no placeholder and can adopt the snapshot.
        if (lastMessage?.role !== MessageRole.Assistant) {
          if (!findStreamingMessage(latestState.messages)) {
            setMessages(conversationMessages);
          }
          completeReconciliation();
          return;
        }

        // A trailing PENDING interactive message means the turn is blocked on
        // the user, not completed: surface the authoritative transcript (card
        // included) but stay in the awaiting state — the restarted
        // subscription streams the rest of the turn once the user responds.
        // The temp streaming placeholder is dropped; respondToMessage
        // recreates one when the turn resumes.
        if (isPendingInteractiveMessage(lastMessage)) {
          setMessages(conversationMessages);
          completeReconciliation();
          return;
        }

        terminalResultVersionRef.current += 1;
        completeCurrentTurn();
        setMessages(conversationMessages);
        completeReconciliation();
        setLastUserMessage(null);
      } catch (reconcileError) {
        // Keep sends blocked. A later reconnect or app resume retries the
        // authoritative read; stream events alone cannot prove that no event was
        // missed before the new subscription became active.
        reportError(
          'Failed to reconcile household chat after reconnect',
          reconcileError instanceof Error ? reconcileError : undefined
        );
        if (
          navigationVersionRef.current === navigationVersion &&
          reconciliationVersionRef.current === reconciliationVersion
        ) {
          setError('Chat stream disconnected.');
        }
      }
    },
    [completeCurrentTurn, getConversationState, loadConversation, reportError]
  );

  const { restart: restartSubscription } =
    useSubscribeHouseholdChatSubscription({
      skip: !conversationId,
      variables: conversationId ? { conversationId } : undefined,
      onData: ({ data }) => {
        const event = data.data?.subscribeHouseholdChat;
        if (!event) {
          return;
        }

        if (event.activity) {
          const activity = event.activity;
          if (activity.toolName === PRESENTATION_EDIT_TOOL_NAME) {
            presentationEditInCurrentTurnRef.current = true;
          }
          setMessages((previousMessages) =>
            appendStreamActivity(previousMessages, activity)
          );
        }

        if (event.textDelta) {
          const textDelta = event.textDelta;
          setMessages((previousMessages) =>
            appendStreamText(previousMessages, textDelta)
          );
        }

        if (event.message) {
          const incomingMessage = event.message;
          const currentState = getConversationState();
          const respondedMessageId =
            isPendingInteractiveMessage(incomingMessage) &&
            currentState.respondingToMessage?.messageId !== incomingMessage.id
              ? currentState.respondingToMessage?.messageId
              : null;

          if (respondedMessageId) {
            // Reaching the next interactive card proves the prior response was
            // accepted, even if its status-update event was missed.
            setRespondingToMessage(null);
          }

          setMessages((previousMessages) => {
            const nextMessages = upsertStreamMessage(
              previousMessages,
              incomingMessage
            );
            if (!respondedMessageId) {
              return nextMessages;
            }

            // Resolve the stale prior card locally until the next conversation
            // fetch supplies its persisted response row.
            return nextMessages.map((message) =>
              message.id === respondedMessageId &&
              message.status === MessageStatus.Pending
                ? { ...message, status: MessageStatus.Responded }
                : message
            );
          });
        }

        if (event.result) {
          const result = event.result;
          // A terminal result that belongs to a conversation the user has since
          // navigated away from would otherwise replace the active transcript.
          const resultConversationId = result.conversation?.id;
          if (
            resultConversationId &&
            resultConversationId !== getConversationState().conversationId
          ) {
            return;
          }

          terminalResultVersionRef.current += 1;
          completeCurrentTurn();
          setMessages((previousMessages) =>
            toResultMessages(result, previousMessages)
          );
          setError(null);
          setLastUserMessage(null);
        }
      },
      onError: (subscriptionError) => {
        // The backend rotates long-lived subscriptions (each is capped at a
        // fixed timeout) and signals it with a generic "reconnect subscription"
        // error. Re-establish the stream instead of surfacing a failure,
        // mirroring the workflow tracker provider.
        const isReconnectRequest =
          isReconnectSubscriptionError(subscriptionError);
        if (isReconnectRequest) {
          diagnostics.debug(
            '[HouseholdChat] Subscription asked to reconnect; restarting'
          );
        } else {
          reportError('Household chat subscription failed', subscriptionError);
          setError('Chat stream disconnected.');
        }

        // Apollo's restart creates a new GraphQL subscription, but Valkey does
        // not replay events from the disconnected interval. Keep the composer
        // blocked until a network-only conversation query repairs that gap.
        subscriptionReconciliationRequiredRef.current = true;
        setIsReconcilingSubscription(true);
        clearTimeout(subscriptionRestartTimeoutRef.current);
        const restartAndReconcile = () => {
          restartSubscription();
          void reconcileAfterReconnect();
        };
        if (isReconnectRequest) {
          restartAndReconcile();
          return;
        }

        // Wait for the next task, not a microtask: graphql-ws shares close
        // listeners across sockets. Restarting during a socket error lets the
        // old socket's close reject the new connection and leak a CloseEvent
        // promise rejection (INTERFACE-10B8).
        const navigationVersion = navigationVersionRef.current;
        subscriptionRestartTimeoutRef.current = setTimeout(() => {
          if (navigationVersionRef.current !== navigationVersion) return;
          restartAndReconcile();
        }, 0);
      },
    });

  // A backgrounded tab can be left holding a dead socket that never errors
  // (no keepalive traffic flows on an idle stream). On resume, force a fresh
  // subscription and re-sync any turn that resolved while away.
  const handleAppResume = useCallback(() => {
    if (!getConversationState().conversationId) {
      return;
    }
    subscriptionReconciliationRequiredRef.current = true;
    setIsReconcilingSubscription(true);
    restartSubscription();
    void reconcileAfterReconnect();
  }, [getConversationState, reconcileAfterReconnect, restartSubscription]);

  useRefetchOnAppResume(handleAppResume, {
    enabled: !!conversationId,
    label: 'HouseholdChat',
  });

  const openChat = useCallback(() => {
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const registerFullScreenModalHost = useCallback(() => {
    setFullScreenModalHostCount((count) => count + 1);
  }, []);

  const unregisterFullScreenModalHost = useCallback(() => {
    setFullScreenModalHostCount((count) => Math.max(0, count - 1));
  }, []);

  const clearPendingScroll = useCallback(() => {
    setPendingScrollToMessageId(null);
  }, []);

  const startNewConversation = useCallback(() => {
    navigationVersionRef.current += 1;
    presentationEditInCurrentTurnRef.current = false;
    setConversationId(null);
    setConversationKind(null);
    setMessages([]);
    setError(null);
    setLastUserMessage(null);
    setPendingScrollToMessageId(null);
    setRespondingToMessage(null);
    respondInFlightRef.current = false;
    subscriptionReconciliationRequiredRef.current = false;
    reconcileAfterSendRef.current = false;
    reconciliationVersionRef.current += 1;
    setIsReconcilingSubscription(false);
    // A fresh conversation has no saved mode yet, so reset to the default.
    setChatMode(ChatMode.Quick);
    // Personas are scoped to the conversation the user picked them in.
    setChatPersona(null);
    // A fresh conversation re-attaches the viewed object even if the user
    // dismissed it in the previous one.
    setDismissedViewedContextKey(null);
    // Clear messagesLoading too: starting a new conversation can interrupt an
    // in-flight selectConversation fetch, whose `finally` will no longer clear
    // the flag (the conversation id has changed), which would otherwise leave
    // the composer permanently disabled.
    setMessagesLoading(false);
    setView('conversation');
  }, []);

  const showConversationList = useCallback(() => {
    if (isSingleConversation) return;
    setView('list');
  }, [isSingleConversation]);

  const selectConversation = useCallback(
    async (
      nextConversationId: string,
      // Callers that already know the conversation's kind (the conversation
      // list) pass it so kind-dependent chrome is correct on the first render
      // instead of after the history loads. Omitted by callers that only have
      // an id (the notification deep link).
      knownKind?: ConversationKind | null
    ) => {
      // Reset in-flight streaming state (same teardown as a new conversation),
      // then activate the selected conversation and load its messages.
      navigationVersionRef.current += 1;
      presentationEditInCurrentTurnRef.current = false;
      const navigationVersion = navigationVersionRef.current;
      const terminalResultVersion = terminalResultVersionRef.current;
      setError(null);
      setLastUserMessage(null);
      setPendingScrollToMessageId(null);
      setRespondingToMessage(null);
      respondInFlightRef.current = false;
      subscriptionReconciliationRequiredRef.current = false;
      reconcileAfterSendRef.current = false;
      reconciliationVersionRef.current += 1;
      setIsReconcilingSubscription(false);

      setMessages([]);
      setMessagesLoading(true);
      setView('conversation');
      setConversationId(nextConversationId);
      setConversationKind(knownKind ?? null);
      // Personas aren't persisted server-side, so a selected conversation
      // starts back at the default (no-persona) behavior.
      setChatPersona(null);

      try {
        const queryResult = await loadConversation({
          variables: { conversationId: nextConversationId },
        });

        const stillCurrent = navigationVersionRef.current === navigationVersion;
        // If the selected conversation was still generating, its terminal
        // stream result may have landed while the fetch was in flight; that
        // result carries the full, fresher conversation, so the fetched
        // history (and any fetch error) is moot.
        const superseded =
          terminalResultVersionRef.current !== terminalResultVersion;
        if (queryResult.error) {
          reportError(
            'Failed to load household chat conversation',
            queryResult.error
          );
          if (stillCurrent && !superseded) {
            setError('Unable to load conversation.');
          }
        } else if (
          stillCurrent &&
          !superseded &&
          !findStreamingMessage(getConversationState().messages)
        ) {
          // Apply only if the user hasn't switched conversations or started
          // sending while the fetch was in flight.
          const node = queryResult.data?.node;
          const conversation =
            node?.__typename === 'Conversation' ? node : null;
          setMessages(toConversationMessages(conversation));
          setConversationKind(conversation?.kind ?? null);
          // Adopt the conversation's saved answer-depth mode (Quick when it has
          // none yet) so the selector reflects how this conversation last ran.
          setChatMode(conversation?.mode ?? ChatMode.Quick);
        }
      } catch (loadError) {
        reportError(
          'Failed to load household chat conversation',
          loadError instanceof Error ? loadError : undefined
        );
        if (
          navigationVersionRef.current === navigationVersion &&
          terminalResultVersionRef.current === terminalResultVersion
        ) {
          setError('Unable to load conversation.');
        }
      } finally {
        if (navigationVersionRef.current === navigationVersion) {
          setMessagesLoading(false);
        }
      }
    },
    [getConversationState, loadConversation, reportError]
  );

  useOpenChatConversationFromDeepLink({ openChat, selectConversation });

  // The owner can supply its conversation after mount, for example when a
  // query finds it. A null ID never replaces a conversation that the first
  // send created.
  useEffect(() => {
    if (
      !singleConversationID ||
      singleConversationID === getConversationState().conversationId
    ) {
      return;
    }
    void selectConversation(singleConversationID);
  }, [getConversationState, selectConversation, singleConversationID]);

  const ensureConversation = useCallback(async () => {
    const currentConversationId = getConversationState().conversationId;
    if (currentConversationId) {
      return currentConversationId;
    }

    // selectConversation / startNewConversation bump the navigation version.
    // If either
    // runs while we await below, adopting the created conversation would
    // hijack the user's navigation — abort the send instead (the
    // interrupting action already cleared the optimistic messages and
    // in-flight state).
    const navigationVersion = navigationVersionRef.current;

    const { data } = await startConversation({
      variables: {
        input: {
          householdID: householdId,
          // Conversation-level context about where the user started the chat,
          // distinct from their first typed message.
          metadata: {
            url: window.location.href,
          },
        },
      },
    });

    const resolvedConversation =
      data?.startHouseholdChatStream.conversation ?? null;

    if (!resolvedConversation) {
      throw new Error('Missing conversation from startHouseholdChatStream');
    }

    if (navigationVersionRef.current !== navigationVersion) {
      return null;
    }

    const nextConversationId = resolvedConversation.id;
    setConversationId(nextConversationId);
    setConversationKind(resolvedConversation.kind ?? null);

    // Let the subscription mount before the send mutation starts streaming.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });

    // The user can also navigate during the frame we just yielded; their
    // action already re-pointed conversationId, so just drop the send.
    if (navigationVersionRef.current !== navigationVersion) {
      return null;
    }

    return nextConversationId;
  }, [getConversationState, householdId, startConversation]);

  // A card response stays "in flight" until the card's status flip arrives
  // (stream event or refetch), so terminal cards can never be re-submitted
  // through a stale enabled button. Clear it once the card leaves PENDING or
  // the transcript no longer contains it (conversation switch).
  useEffect(() => {
    const inFlight = respondingToMessage;
    if (!inFlight) {
      return;
    }
    const target = messages.find(
      (message) => message.id === inFlight.messageId
    );
    if (!target || target.status !== MessageStatus.Pending) {
      setRespondingToMessage(null);
    }
  }, [messages, respondingToMessage]);

  // After a respond failure on an idle transcript, re-read the conversation:
  // the most common failure is a card that resolved elsewhere (another tab,
  // expiry) whose flip this client missed, and the persisted state is
  // authoritative.
  const refreshConversationAfterRespondFailure = useCallback(async () => {
    const targetConversationId = getConversationState().conversationId;
    const navigationVersion = navigationVersionRef.current;
    if (!targetConversationId) {
      return;
    }
    try {
      const queryResult = await loadConversation({
        variables: { conversationId: targetConversationId },
      });
      // Apply only while still idle on the same conversation; a live turn's
      // stream events are fresher than this snapshot.
      const latestState = getConversationState();
      if (
        navigationVersionRef.current !== navigationVersion ||
        latestState.conversationId !== targetConversationId ||
        findStreamingMessage(latestState.messages)
      ) {
        return;
      }
      const node = queryResult.data?.node;
      const conversation = node?.__typename === 'Conversation' ? node : null;
      if (conversation) {
        setMessages(toConversationMessages(conversation));
      }
    } catch (refreshError) {
      reportError(
        'Failed to refresh household chat after respond failure',
        refreshError instanceof Error ? refreshError : undefined
      );
    }
  }, [getConversationState, loadConversation, reportError]);

  const respondToMessage = useCallback(
    async (input: HouseholdRespondToChatMessageInput) => {
      const { messageID: messageId, responseAction } = input;
      // Suppress duplicate submissions while a response is in flight.
      if (
        respondInFlightRef.current ||
        getConversationState().respondingToMessage
      ) {
        return false;
      }
      respondInFlightRef.current = true;
      setRespondingToMessage({ messageId, responseAction });

      // selectConversation / startNewConversation bump the navigation version.
      // If either
      // runs while the mutation is in flight, the navigation already reset
      // the streaming state for the newly active conversation — restoring
      // this turn's placeholder and awaiting flag would pollute its
      // transcript and block its composer.
      const navigationVersion = navigationVersionRef.current;
      const terminalResultVersion = terminalResultVersionRef.current;

      try {
        await respondToChatMessage({
          variables: {
            input,
          },
        });

        if (navigationVersionRef.current !== navigationVersion) {
          return true;
        }
        // The resumed turn already finished while the mutation was settling
        // (its terminal result landed); restoring a placeholder and the
        // awaiting flag now would clobber the completed transcript. The
        // card's status flip alone is NOT this signal — it is the first
        // event of the resumed turn, so it can beat the mutation response
        // while activities and the answer are still streaming.
        if (terminalResultVersionRef.current !== terminalResultVersion) {
          return true;
        }

        // The mutation only routes the answer to the waiting workflow — the
        // card's status flip and the response row arrive via the stream (or
        // a refetch), so the in-flight state stays set until they do. The
        // same agent turn now resumes and ends with a normal result event;
        // when there is no live streaming placeholder (the card was
        // recovered after a refresh or reconnect), restore one so the
        // resumed turn's activity trace and answer stream in.
        setError(null);
        if (!findStreamingMessage(getConversationState().messages)) {
          setMessages((previousMessages) => [
            ...previousMessages,
            createStreamingAssistantMessage(),
          ]);
        }
        return true;
      } catch (respondError) {
        setRespondingToMessage(null);
        // The user already left this conversation; feedback about the old
        // card (and a refetch on its behalf) would only confuse the new one.
        if (navigationVersionRef.current !== navigationVersion) {
          return false;
        }
        reportError(
          'Failed to respond to household chat message',
          respondError instanceof Error ? respondError : undefined
        );
        // Surface the server's public reason when there is one (permission
        // errors, "no longer available"); fall back to a generic message.
        const publicMessage =
          respondError instanceof ApolloError &&
          respondError.graphQLErrors[0]?.message
            ? respondError.graphQLErrors[0].message
            : 'Unable to submit your response.';
        showFeedback(publicMessage);
        if (!findStreamingMessage(getConversationState().messages)) {
          void refreshConversationAfterRespondFailure();
        }
        return false;
      } finally {
        if (navigationVersionRef.current === navigationVersion) {
          respondInFlightRef.current = false;
        }
      }
    },
    [
      refreshConversationAfterRespondFailure,
      getConversationState,
      reportError,
      respondToChatMessage,
      showFeedback,
    ]
  );

  const sendMessage = useCallback(
    async (
      message: string,
      explicitContextObjectId?: string,
      options?: HouseholdChatSendOptions
    ) => {
      let trimmedMessage = message.trim();
      // Message storage requires non-empty content for template-only requests.
      if (!trimmedMessage && options?.additionalInput?.withPromptTemplate) {
        trimmedMessage = TEMPLATE_ONLY_MESSAGE_CONTENT;
      }
      if (!trimmedMessage) {
        return null;
      }

      const currentState = getConversationState();
      // Validation-questions conversations take no free-form turns: the
      // composer is hidden for them, so this only guards races (a send started
      // just before the kind resolved).
      if (currentState.isValidationQuestionsConversation) {
        return null;
      }

      if (
        sendInFlightRef.current ||
        subscriptionReconciliationRequiredRef.current ||
        findStreamingMessage(currentState.messages) ||
        currentState.respondingToMessage
      ) {
        showFeedback('Wait for the current response to finish.');
        return null;
      }

      // A pending card blocks the conversation server-side (the user answers
      // through the card, not the composer); guard here so races surface
      // gracefully instead of as a mutation error.
      if (currentState.pendingInteractiveMessage) {
        showFeedback('Respond to the pending message to continue.');
        return null;
      }

      presentationEditInCurrentTurnRef.current = false;
      sendInFlightRef.current = true;
      setError(null);
      setLastUserMessage(trimmedMessage);

      // The specialized persona this turn runs with; null outside the persona
      // POC or when the user kept the default behavior.
      const sendPersona = personaSelectorEnabled ? chatPersona : null;
      // The mode this turn is sent (and tracked) with; null when modes are
      // retired by the no-modes flag or when a persona drives the turn.
      const sendMode = getSendMode({
        chatMode,
        chatModesEnabled,
        sendPersona,
      });

      const { assistantMessageId, optimisticMessages, userMessageId } =
        createOptimisticTurn(
          trimmedMessage,
          options?.additionalInput?.withPromptTemplate ?? undefined,
          options?.referenceImage ? [options.referenceImage] : undefined
        );

      // Add the assistant placeholder immediately (not on first token) so the
      // activity trace has somewhere to render while tools run pre-answer.
      setMessages((previousMessages) => [
        ...previousMessages,
        ...optimisticMessages,
      ]);
      setPendingScrollToMessageId(userMessageId);

      let resolvedConversationId = currentState.conversationId;
      let reconcileRejectedSend = false;
      try {
        resolvedConversationId = await ensureConversation();
        if (!resolvedConversationId) {
          return null;
        }
        options?.onConversationReady?.(resolvedConversationId);
        const contextObjectId =
          explicitContextObjectId ?? viewedContext?.objectId ?? null;
        const response = await sendChatMessage({
          context: options?.referenceImage ? { sensitive: true } : undefined,
          variables: {
            input: {
              ...latestGetAdditionalSendInput.current?.(),
              ...options?.additionalInput,
              uploadedFiles: options?.referenceImage
                ? [options.referenceImage]
                : undefined,
              context: contextObjectId ? { objectID: contextObjectId } : null,
              conversationID: resolvedConversationId,
              householdID: householdId,
              message: trimmedMessage,
              mode: sendMode,
              persona: sendPersona,
            },
          },
        });

        const nextWorkflowTrackerId =
          response.data?.sendHouseholdChatMessage.workflowTracker.id ?? null;

        if (!nextWorkflowTrackerId) {
          throw new Error(
            'Missing workflow tracker from sendHouseholdChatMessage'
          );
        }
        trackUserEvent('household_chat chat question asked', {
          message: trimmedMessage,
          mode: sendMode,
          persona: sendPersona,
          householdId,
          conversationId: resolvedConversationId,
          viewedContextType: viewedContext?.item.type ?? null,
          success: true,
        });
        return {
          conversationId: resolvedConversationId,
          workflowTrackerId: nextWorkflowTrackerId,
        };
      } catch (sendError) {
        reportError(
          'Household chat send failed',
          sendError instanceof Error ? sendError : undefined
        );
        setError('Unable to send message.');
        setMessages((previousMessages) =>
          removeOptimisticTurn(
            previousMessages,
            userMessageId,
            assistantMessageId
          )
        );
        setPendingScrollToMessageId(null);
        // BAD_INPUT can mean the server has an interactive card that this
        // client missed. Refresh instead of coupling recovery to unstable
        // public error text.
        reconcileRejectedSend =
          sendError instanceof ApolloError &&
          getGraphQLErrorsByStatusCode(sendError, GraphQLStatusCodes.BAD_INPUT)
            .length > 0;
        if (reconcileRejectedSend) {
          subscriptionReconciliationRequiredRef.current = true;
          setIsReconcilingSubscription(true);
        }
        trackUserEvent('household_chat chat question asked', {
          message: trimmedMessage,
          mode: sendMode,
          persona: sendPersona,
          householdId,
          conversationId: resolvedConversationId,
          success: false,
        });
        options?.onError?.(sendError);
      } finally {
        sendInFlightRef.current = false;
        if (
          reconcileRejectedSend ||
          reconcileAfterSendRef.current ||
          subscriptionReconciliationRequiredRef.current
        ) {
          void reconcileAfterReconnect({
            preserveError: reconcileRejectedSend,
          });
        }
      }

      return null;
    },
    [
      chatMode,
      chatModesEnabled,
      chatPersona,
      ensureConversation,
      getConversationState,
      householdId,
      latestGetAdditionalSendInput,
      personaSelectorEnabled,
      reportError,
      reconcileAfterReconnect,
      sendChatMessage,
      showFeedback,
      trackUserEvent,
      viewedContext,
    ]
  );

  const awaitingFirstToken =
    isStartingConversation ||
    isSendingMessage ||
    Boolean(streamingMessage && streamingMessage.content === '');

  useOpenChatConversationFromDeepLink({ openChat, selectConversation });
  useSendInitialHouseholdChatMessage({
    conversationId,
    openChat,
    sendMessage,
    startNewConversation,
  });

  const value = useMemo<HouseholdChatContextValue>(
    () => ({
      refreshConversation,
      awaitingFirstToken,
      chatMode,
      chatModesEnabled,
      chatPersona,
      clearPendingScroll,
      closeChat,
      conversationId,
      dismissViewedContext,
      error,
      hasDismissedViewedContext,
      householdId,
      isChatOpen,
      isReconcilingSubscription,
      isFullScreenModalHostMounted: fullScreenModalHostCount > 0,
      isSingleConversation,
      isTranscriptMessageHidden,
      isValidationQuestionsConversation,
      lastUserMessage,
      messages,
      messagesLoading,
      openChat,
      pendingInteractiveMessage,
      pendingScrollToMessageId,
      personaSelectorEnabled,
      presentationEditCompletedVersion,
      registerFullScreenModalHost,
      respondingToMessage,
      respondToMessage,
      restoreViewedContext,
      selectConversation,
      sendMessage,
      setChatMode,
      setChatPersona,
      setModalViewedContext,
      showConversationList,
      startNewConversation,
      streamingMessageId,
      unregisterFullScreenModalHost,
      view,
      viewedContext,
    }),
    [
      refreshConversation,
      awaitingFirstToken,
      chatMode,
      chatModesEnabled,
      chatPersona,
      clearPendingScroll,
      closeChat,
      conversationId,
      dismissViewedContext,
      error,
      hasDismissedViewedContext,
      householdId,
      isChatOpen,
      isReconcilingSubscription,
      fullScreenModalHostCount,
      isSingleConversation,
      isTranscriptMessageHidden,
      isValidationQuestionsConversation,
      lastUserMessage,
      messages,
      messagesLoading,
      openChat,
      pendingInteractiveMessage,
      pendingScrollToMessageId,
      personaSelectorEnabled,
      presentationEditCompletedVersion,
      registerFullScreenModalHost,
      respondingToMessage,
      respondToMessage,
      restoreViewedContext,
      selectConversation,
      sendMessage,
      setChatMode,
      showConversationList,
      startNewConversation,
      streamingMessageId,
      unregisterFullScreenModalHost,
      view,
      viewedContext,
    ]
  );

  return (
    <HouseholdChatContext.Provider value={value}>
      {children}
    </HouseholdChatContext.Provider>
  );
}
